import React from 'react'
import { RouteComponentProps } from 'react-router-dom'
import { useApolloClient, useLazyQuery } from '@apollo/client'
import papa from 'papaparse'
import {
  Users as UsersType,
  UsersVariables,
  UserTypeForFilter,
  useAuth,
  getFormattedDateAndTime,
  SortDirectionEnum,
  useStateWithDebounce,
  GroupMemberRole,
  GroupsFilterInput,
  UserPermissions,
  AllUsers,
  AllUsers_users_users,
} from '@modmd/data'
import { useSearchState } from 'internal-portal/utils/useSearchState'
import DashboardPage from 'internal-portal/sharedComponents/DashboardPage'
import { CompaniesDropdown } from 'sharedComponents/CompaniesDropdown'
import Datalist from 'components/Datalist'
import Card from 'components/Card'
import Button from 'components/Button'
import { Box, Container } from 'components/Layout'
import TextInput from 'components/TextInput'
import Icon from 'components/Icon'
import { DEVICE } from 'theme'
import Label from 'components/Label'
import { ROUTES } from 'internal-portal/constants/routes'
import Pagination from 'components/Pagination'
import { useIsMinDevice } from 'utils/hooks/useMedia'
import { Dialog } from 'components/Dialog'
import { DateInput } from 'components/DateInput'
import { SelectInput } from 'components/SelectInput'
import { GroupsDropdown } from 'sharedComponents/GroupsDropdown'
import { GROUP_ROLES_OPTIONS } from 'internal-portal/routes/TestSessions/utils'
import { USERS } from './operations'
import { UserInvitation, OnInviteParams } from './UserInvitation'
import { useToaster } from 'utils/useToaster'

const SKELETON_COLUMNS = 4
export const USERS_PAGE_LENGTH = 10

const getUserTypeFromPathName = (pathname: string) =>
  Object.values(UserTypeForFilter).find((type) => pathname.includes(type.toLowerCase())) || null

const Users: React.VFC<RouteComponentProps> = ({ history, location }) => {
  const searchState = useSearchState<{
    emailOrNameLike?: string
    page?: string
    type?: string
    testDateFrom?: string
    testDateTo?: string
    companyId?: string
    groupId?: string
    groupRole?: GroupMemberRole
  }>()
  const { getUserGroupsWithRole, hasPermission } = useAuth()
  const isDesktop = useIsMinDevice(DEVICE.DESKTOP)
  const [isOpenDrawer, setIsOpenDrawer] = React.useState(false)
  const [isDeletedOption, setIsDeletedOption] = React.useState(false)
  const [fetchCsvLoading, setFetchCsvLoading] = React.useState(false)
  const [getUsers, { data: usersData, loading: usersLoading }] = useLazyQuery<
    UsersType,
    UsersVariables
  >(USERS)
  const [emailOrNameLike, setEmailOrNameLike] = useStateWithDebounce(
    searchState.data.emailOrNameLike,
    (emailOrNameLike) => {
      searchState.setSearchState({ emailOrNameLike, page: 1 }, false, true)
    }
  )

  const client = useApolloClient()

  const { setToastMessage } = useToaster()

  const { groups } = getUserGroupsWithRole({
    roles: [GroupMemberRole.Member, GroupMemberRole.Coordinator, GroupMemberRole.Manager],
  })

  const getGroupIds = React.useCallback(() => {
    if (
      !hasPermission([UserPermissions.SEE_ALL_USERS_INFORMATION]) &&
      hasPermission([UserPermissions.SEE_EDIT_GROUP_USERS_INFORMATION])
    )
      return groups.length ? groups.map(({ id }) => id) : undefined
    return undefined
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const usersQueryVariables = React.useMemo(
    () => ({
      pagination: {
        from: (Number(searchState.data.page || 1) - 1) * USERS_PAGE_LENGTH,
        length: USERS_PAGE_LENGTH,
        sortBy: 'id',
        direction: SortDirectionEnum.DESC,
      },
      filterData: {
        type: getUserTypeFromPathName(location.pathname),
        emailOrNameLike: searchState.data.emailOrNameLike,
        isDeleted: isDeletedOption,
        includeCity: true,
        groupIds: getGroupIds(),
        ...(searchState.data.testDateFrom || searchState.data.testDateTo
          ? {
              testDateBetween: [
                searchState.data.testDateFrom ?? '',
                searchState.data.testDateTo ?? '',
              ],
            }
          : {}),
        ...(searchState.data.companyId ? { Company: { id: searchState.data.companyId } } : {}),
        ...(searchState.data?.groupId || searchState.data?.groupRole
          ? {
              Group: {
                ...(searchState.data?.groupId && { id: searchState.data?.groupId }),
                ...(searchState.data?.groupRole && {
                  memberUserGroupRoles: searchState.data?.groupRole,
                }),
              } as GroupsFilterInput,
            }
          : {}),
      },
    }),
    [
      searchState.data.page,
      searchState.data.emailOrNameLike,
      searchState.data.testDateFrom,
      searchState.data.testDateTo,
      searchState.data.companyId,
      searchState.data?.groupId,
      searchState.data?.groupRole,
      location.pathname,
      getGroupIds,
      isDeletedOption,
    ]
  )

  React.useEffect(() => {
    getUsers({
      variables: usersQueryVariables,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usersQueryVariables])

  const handleCustomInputChange = React.useCallback(
    ({ name, value }: { name: string; value: string | undefined }) => {
      searchState.setSearchState({ [name]: value, page: 1 }, false, true)
    },
    [searchState]
  )

  const handleOpenDrawer = React.useCallback(() => {
    setIsOpenDrawer(true)
  }, [])

  const handleCloseDrawer = React.useCallback(() => {
    setIsOpenDrawer(false)
  }, [setIsOpenDrawer])
  const handleInviteSuccess = React.useCallback(
    ({ userId, companyId }: OnInviteParams) => {
      handleCloseDrawer()
      if (companyId) {
        history.push(`/${ROUTES.COMPANIES}/${companyId}`)
      } else if (userId) {
        history.push(`/${ROUTES.USERS}/${userId}`)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleCloseDrawer]
  )

  const userCount = usersData?.users?.pagination.total ?? 0
  const pageLength = usersData?.users?.pagination.length ?? 0

  const createDownloadableLink = (blob: Blob, fileName: string) => {
    const link = document.createElement('a')
    link.href = window.URL.createObjectURL(blob)
    link.setAttribute('download', `${fileName}.csv`)
    document.body.appendChild(link)

    const click = () => {
      link.click()
      document.body.removeChild(link)
    }

    return { link, click }
  }

  const parseResultIntoCSVModel = (users: AllUsers_users_users[] | null) =>
    users?.map((user) => {
      const { firstName, lastName, birthDate, city, email, lastTestedAt, state } = user
      return {
        'First Name': firstName,
        'Last Name': lastName,
        'Birth Date': getFormattedDateAndTime(birthDate),
        City: city,
        State: state,
        Email: email,
        'Last Tested At': lastTestedAt ? getFormattedDateAndTime(lastTestedAt) : 'Not tested yet',
      }
    })

  const generateCsv = (data: object[]) => {
    const csv = papa.unparse(data, {
      header: true,
    })

    return new Blob([csv], { type: 'text/csv' })
  }

  const handleDownloadCsv = (result: AllUsers) => {
    if (result?.users?.users && result?.users?.users.length < 1) {
      setToastMessage('No results found', 'danger')

      return
    }

    const parsedResult = parseResultIntoCSVModel(result?.users?.users)
    const dataBlob = generateCsv(parsedResult!)

    createDownloadableLink(dataBlob, `Users-${Date.now()}`).click()
  }

  const handleGetAllUsers = async () => {
    setFetchCsvLoading(true)

    const { data } = await client.query({
      query: USERS,
      variables: {
        ...usersQueryVariables,
        pagination: {
          from: 0,
          length: userCount,
        },
      },
    })

    setFetchCsvLoading(false)
    handleDownloadCsv(data as AllUsers)
  }

  return (
    <DashboardPage
      Header={
        hasPermission([UserPermissions.INVITE_CREATE_USER]) && (
          <Box display="flex" justifyContent="flex-end" p="1rem">
            <Button onClick={() => handleGetAllUsers()} isFetching={fetchCsvLoading} mr="1rem">
              Download CSV
            </Button>
            <Button onClick={handleOpenDrawer} leftIcon={<Icon.Plus size="L" />}>
              Invite / Create user
            </Button>
          </Box>
        )
      }
    >
      <Dialog
        variant={isDesktop ? 'right' : 'fullscreen'}
        maxWidth={isDesktop ? '500px' : '100%'}
        isOpen={isOpenDrawer}
        onDismiss={handleCloseDrawer}
      >
        <UserInvitation
          userType={getUserTypeFromPathName(location.pathname) as UserTypeForFilter}
          onInvite={handleInviteSuccess}
        />
      </Dialog>
      <Container>
        <Card>
          <Datalist
            isLoading={usersLoading}
            skeletonProps={{
              columns: SKELETON_COLUMNS,
              rows: USERS_PAGE_LENGTH,
            }}
            data={
              usersData?.users.users.map((item) => ({
                ...item,
                birthDate: getFormattedDateAndTime(item.birthDate),
                name: `${item.firstName || ''} ${item.lastName || ''}`,
              })) || []
            }
            header={
              <Datalist.Title
                rightContent={
                  <Box
                    display="grid"
                    gridGap="1rem"
                    gridTemplateColumns={{
                      TABLET: 'repeat(2, 1fr)',
                      DESKTOP: 'repeat(5, 1fr)',
                    }}
                  >
                    <TextInput
                      name="emailOrNameLike"
                      placeholder="Name or email"
                      value={emailOrNameLike}
                      onChange={(event) => {
                        setEmailOrNameLike(event.target.value)
                      }}
                      leftIcon={<Icon.Search />}
                      hasBottomContent
                    />
                    <CompaniesDropdown
                      value={searchState.data.companyId}
                      onChange={(id) => {
                        handleCustomInputChange({
                          name: 'companyId',
                          value: String(id),
                        })
                        if (!id) {
                          const { groupId, companyId, ...rest } = searchState?.data
                          searchState.setSearchState(rest)
                        }
                      }}
                      hasBottomContent
                    />
                    <DateInput
                      id="testDateFrom"
                      name="testDateFrom"
                      label="Test Date From"
                      value={
                        searchState.data.testDateFrom
                          ? new Date(searchState.data.testDateFrom)
                          : null
                      }
                      onChange={(date) => {
                        handleCustomInputChange({
                          name: 'testDateFrom',
                          value: date ? getFormattedDateAndTime(date) : undefined,
                        })
                      }}
                      hasBottomContent
                      isFullWidth
                    />
                    <DateInput
                      id="testDateTo"
                      name="testDateTo"
                      label="Test Date To"
                      value={
                        searchState.data.testDateTo ? new Date(searchState.data.testDateTo) : null
                      }
                      onChange={(date) => {
                        handleCustomInputChange({
                          name: 'testDateTo',
                          value: date ? getFormattedDateAndTime(date) : undefined,
                        })
                      }}
                      hasBottomContent
                      isFullWidth
                    />
                    <SelectInput
                      name="groupRole"
                      label="Group Member Role"
                      onChange={(event) => {
                        handleCustomInputChange({
                          name: 'groupRole',
                          value: event.target.value,
                        })
                      }}
                      value={searchState.data?.groupRole || ''}
                      options={GROUP_ROLES_OPTIONS}
                    />
                    <GroupsDropdown
                      value={searchState.data.groupId}
                      onChange={(id) => {
                        handleCustomInputChange({
                          name: 'groupId',
                          value: id,
                        })
                      }}
                      companyId={searchState.data.companyId}
                      hasBottomContent
                    />
                    <SelectInput
                      name="isDeleted"
                      label="User option"
                      onChange={() => {
                        setIsDeletedOption(!isDeletedOption)
                      }}
                      value={isDeletedOption.toString()}
                      options={[
                        {
                          value: 'false',
                          label: 'Active users',
                        },
                        {
                          value: 'true',
                          label: 'Deleted users',
                        },
                      ]}
                    />
                  </Box>
                }
              >
                Users
              </Datalist.Title>
            }
            shouldRenderTableHeader={isDesktop}
            showActionIndicators
            renderTableHeader={() => (
              <Datalist.Row gridTemplateColumns="repeat(6, 1fr)">
                <Datalist.HeaderCell label="Name" />
                <Datalist.HeaderCell label="Birthdate" />
                <Datalist.HeaderCell label="City" />
                <Datalist.HeaderCell label="State" />
                <Datalist.HeaderCell label="Email" />
                <Datalist.HeaderCell label="Last tested at" />
              </Datalist.Row>
            )}
            renderTableRow={({
              id,
              name,
              birthDate,
              city,
              state,
              lastTestedAt,
              email,
              isCompliant,
            }) => (
              <Datalist.Row
                gridTemplateColumns="repeat(6, 1fr)"
                onClick={() => history.push(`/${ROUTES.USERS}/${id}`)}
              >
                <Datalist.Cell label="Name">{name}</Datalist.Cell>
                <Datalist.Cell label="Birthdate">{birthDate}</Datalist.Cell>
                <Datalist.Cell label="City">{city}</Datalist.Cell>
                <Datalist.Cell label="State">{state}</Datalist.Cell>
                <Datalist.Cell label="Email" useEllipsis>
                  {email}
                </Datalist.Cell>
                <Datalist.Cell label="Last tested at">
                  {!isCompliant ? (
                    <Label indicator="yellow">Pending invitation</Label>
                  ) : (
                    <>
                      {lastTestedAt ? (
                        getFormattedDateAndTime(lastTestedAt)
                      ) : (
                        <Label indicator="blue">Not tested</Label>
                      )}
                    </>
                  )}
                </Datalist.Cell>
              </Datalist.Row>
            )}
          />
        </Card>
        {userCount > pageLength && (
          <Pagination.Wrapper>
            <Pagination
              firstPage={1}
              lastPage={Math.ceil(userCount / pageLength)}
              currentPage={Math.ceil((usersData?.users?.pagination.from || 0) / pageLength) + 1}
              onPageChange={(pageNumber) => {
                searchState.setSearchState({ page: pageNumber }, false)
              }}
            />
          </Pagination.Wrapper>
        )}
      </Container>
    </DashboardPage>
  )
}

export { Users }
